// Utilities for working with the `unknown` type

/**
 * Narrows an `unknown` to an `object`.
 *
 * Note that `typeof x === 'object'` returns `true` in JS if `x` is `null`.
 */
export const isNonNullObject = (x: unknown): x is object => !!x && typeof x === 'object'

type UnknownVals<K extends string> = Record<K, unknown>
type ValsOfType<K extends string, V extends unknown> = Record<K, V>

type HavingProperties<K extends string, T extends object> = T & UnknownVals<K>
type HavingPropertiesOfType<K extends string, V extends unknown, T extends object> = T & ValsOfType<K, V>

/**
 * Narrows a non-null `object` type to a record with a specific key.
 *
 * @example
 * const getId = (x: object) => {
 *   if (hasProperty('id', x)) return x.id
 * }
 */
export const hasProperty = <K extends string, T extends object>(key: K, x: T): x is HavingProperties<K, T> => {
  return key in x
}

/**
 * Narrows an `unknown` type to a record with a specific key.
 *
 * @example
 * const getId = (x: unknown) => {
 *   if (isObjectWithProperty('id', x)) return x.id
 * }
 */
export const isObjectWithProperty = <K extends string, T extends object>(
  key: K,
  x: unknown,
): x is HavingProperties<K, T> => isNonNullObject(x) && hasProperty(key, x)

/**
 * Narrows an `unknown` type to a record with a specific key.
 *
 * @example
 * const getId = (x: unknown) => {
 *   if (isObjectWithProperty('id', x)) return x.id
 * }
 */
export const isObjectWithFunctionProperty = <K extends string, T extends object>(
  key: K,
  x: unknown,
): x is HavingPropertiesOfType<K, Function, T> =>
  isNonNullObject(x) && hasProperty(key, x) && typeof x[key] === 'function'

/**
 * Narrows an `unknown` type to a record with specific keys.
 *
 * @example
 * const getFullName = (x: unknown) => {
 *   if (isObjectWithProperties(['firstName', 'lastName'], x)) {
 *     return `${x.firstName} ${x.lastName}`
 *   }
 * }
 */
export const isObjectWithProperties = <K extends string, T extends object>(
  keys: K[],
  x: unknown,
): x is HavingProperties<K, T> => {
  return isNonNullObject(x) && keys.every((key) => hasProperty(key, x))
}
